import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import Player from 'react-player';
import { BASE_PATHS, className, safeGet, renderText } from 'utils';
import { imagePropTypes, videoPropTypes } from 'utils/proptypes';
import { useLocation } from '@reach/router';
import PlayIcon from './PlayIcon';

import styles from './videoPlayer.module.scss';

const addProtocol = url => (url.startsWith('http') ? url : `https:${url}`);

const VideoPlayer = ({
  url,
  cover,
  contentfulVideo,
  caption,
  title,
  transcriptItems,
  showTranscript,
  isVideoCarousel,
  duration,
  ...props
}) => {
  const [started, setStarted] = useState(false);
  const location = useLocation();

  const { url: imageUrl, title: imageTitle, description: imageDescription } = safeGet(
    cover,
    ['file', 'url'],
    'title',
    'description',
  );
  const { url: contenfulVideoUrl } = safeGet(contentfulVideo, ['file', 'url']);

  const videoUrl = contenfulVideoUrl || url;

  if (!videoUrl) {
    return null;
  }

  const isArticle = location.pathname.includes(BASE_PATHS.insights);

  const videoStyles = isVideoCarousel ? styles.videoCarousel : styles.videoComponent;

  return (
    <figure {...className(!isArticle && 'component', videoStyles)}>
      <section className={styles.wrapOuter} aria-label="application">
        <div className={isVideoCarousel ? styles.carouselInner : styles.wrapInner}>
          <div className={showTranscript ? styles.playerWrapOuter : ''}>
            <div className={styles.playerWrap}>
              <Player
                className={styles.player}
                url={addProtocol(videoUrl)}
                playing={started}
                controls
                height="100%"
                width="100%"
                {...props}
              />
              {!started && imageUrl && (
                <Fragment>
                  <img
                    className={styles.cover}
                    src={imageUrl}
                    alt={imageDescription || imageTitle}
                  />
                  <PlayIcon className={styles.playIcon} />
                  <button
                    className={styles.button}
                    aria-label="Play Video"
                    onClick={() => setStarted(true)}
                  />
                </Fragment>
              )}
            </div>
          </div>
          {showTranscript && (
            <div className={styles.transcriptWrapOuter}>
              <div className={styles.transcriptWrap}>
                {!isVideoCarousel && (
                  <section className={styles.wrapOuter} aria-label="application">
                    <div
                      {...className(
                        styles.wrapInner,
                        styles.durationContainer,
                        showTranscript ? styles.wrapOuterLimit : '',
                      )}>
                      {title && <h1 className={styles.title}>{title}</h1>}
                      <p className={styles.duration}>Duration: {duration}</p>
                    </div>
                  </section>
                )}
                <div className={styles.transcriptBody}>
                  {transcriptItems.map((item, index) => (
                    <div key={`transcript-${index}`} className={styles.transcriptItem}>
                      <p className={styles.timestamp}>{item[0]}</p>
                      <div>
                        <p className={styles.speaker}>{item[2]}</p>
                        <p className={styles.description}>{item[1]}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <section className={styles.wrapOuter} aria-label="application">
        <div {...className(styles.wrapInner, showTranscript && styles.wrapOuterLimit)}>
          {caption && (
            <figcaption className={styles.caption}>{renderText({ json: caption })}</figcaption>
          )}
        </div>
      </section>
    </figure>
  );
};

VideoPlayer.propTypes = {
  title: PropTypes.string,
  url: PropTypes.string,
  cover: imagePropTypes,
  contentfulVideo: videoPropTypes,
  caption: PropTypes.object,
  transcriptItems: PropTypes.array,
  showTranscript: PropTypes.bool,
  isVideoCarousel: PropTypes.bool,
  duration: PropTypes.string,
};

export default VideoPlayer;
